import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import DeleteIcon from "@mui/icons-material/Delete";
import DownloadIcon from "@mui/icons-material/Download";
import EditIcon from "@mui/icons-material/Edit";
import { Box, Collapse, Grid, IconButton, Modal } from "@mui/material";
import { Table } from "antd";
import { useSnackbar } from "notistack";
import { useCallback, useEffect, useRef, useState } from "react";
import LoadingBackdrop from "../components/general/LoadingBackdrop";
import NunitoText from "../components/general/NunitoText";
import ReusableButton from "../components/general/ReusableButton";
import ReusableSelect from "../components/general/ReusableSelect";
import ReusableTextField from "../components/general/ReusableTextField";
import AddNewCompetitionForCertificateModal from "../components/modal/AddNewCompetitionForCertificateModal";
import ConfirmDeleteModal from "../components/modal/ConfirmDeleteModal";
import ViewAndFilterButton from "../components/ViewAndFilterButton";
import { isAdmin } from "../functions/checkrole";
import { deleteCertificateConfiguration } from "../functions/deleteData";
import { cancelAction } from "../functions/general";
import {
  filterConfiguredCertificateTemplates,
  getConfiguredNewCompetitionsInPrintCertificates,
} from "../functions/getData";
import {
  addNewCompetitionInPrintCertificates,
  updateCertificateConfiguration,
} from "../functions/postData";
import { showNotification } from "../functions/snackbar";

const ConfigureCertificate = () => {
  document.title = "Certificate Templates";
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const [onAddCompetition, setOnAddCompetition] = useState(false);
  const [onEditCompetition, setOnEditCompetition] = useState(false);
  const [editingConfig, setEditingConfig] = useState(null);
  const [onDelete, setOnDelete] = useState(false);
  const [selectedConfigId, setSelectedConfigId] = useState(null);
  const cancelAddCompetition = () => cancelAction(setOnAddCompetition);
  const cancelEditCompetition = () => {
    setOnEditCompetition(false);
    setEditingConfig(null);
  };
  const [rawData, setRawData] = useState([]);
  const [formattedData, setFormattedData] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [filterCompetition, setFilterCompetition] = useState(null);
  const [filterRoundType, setFilterRoundType] = useState(null);
  const [competitionFilterOptions, setCompetitionFilterOptions] = useState([]);
  const [loadingFilters, setLoadingFilters] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [filtering, setFiltering] = useState(false);
  const pageSizeRef = useRef(pageSize);

  const formatData = (data) => {
    const result = [];
    data?.map((el) => {
      const formattedObj = {};
      const nestedRows = [];
      formattedObj.id = el?.id;
      formattedObj.configuration_name =
        el?.configuration_name ?? el?.name ?? "-";
      formattedObj.competition_name =
        el?.competition_name ?? el?.round_name ?? "-";
      formattedObj.competition_id = el?.competition_id ?? el?.round_id;
      formattedObj.round_type =
        el?.round_type ??
        (el?.is_team_round ? "Team Round" : "Individual Round");
      const countries = el?.countries ?? [];
      const organizations = el?.organizations ?? [];
      const configRoles = el?.roles ?? [];
      formattedObj.countries_display =
        Array.isArray(countries) && countries.length
          ? countries
              .map((c) => (typeof c === "object" ? c?.name ?? c?.label : c))
              .join(", ")
          : "-";
      formattedObj.organizations_display =
        Array.isArray(organizations) && organizations.length
          ? organizations
              .map((o) => (typeof o === "object" ? o?.name ?? o?.label : o))
              .join(", ")
          : "-";
      const rolesDisplay =
        Array.isArray(configRoles) && configRoles.length
          ? configRoles
              .map((r) => (typeof r === "object" ? r?.name ?? r?.label : r))
              .join(", ")
          : "-";
      formattedObj.roles_display = rolesDisplay;
      const awards = el?.awards ?? el?.templates ?? [];
      awards.forEach((item) => {
        const formattedNestedObj = {};
        formattedNestedObj.award = item?.award ?? "-";
        formattedNestedObj.file_name = item?.file_name ?? "-";
        formattedNestedObj.roles =
          Array.isArray(item?.roles) && item.roles.length
            ? item.roles
                .map((r) => (typeof r === "object" ? r?.name ?? r?.label : r))
                .join(" - ")
            : rolesDisplay;
        formattedNestedObj.show_disclaimer =
          item?.show_disclaimer ?? false ? "Yes" : "No";
        formattedNestedObj.url = item?.url ?? item?.download_url;
        nestedRows.push(formattedNestedObj);
      });

      formattedObj.nestedData = nestedRows;
      result.push(formattedObj);
    });
    return result;
  };

  const listData = useCallback(
    async (page = 1, perPage = 10) => {
      setLoading(true);
      try {
        const params = {
          page,
          per_page: perPage,
          ...(searchText?.trim() && { search: searchText.trim() }),
          ...(filterCompetition?.value != null &&
            filterCompetition?.value !== "" && {
              competition: filterCompetition.value,
            }),
          ...(filterRoundType !== null &&
            filterRoundType !== undefined &&
            filterRoundType !== "" && {
              is_team_round: filterRoundType === 1 || filterRoundType === "1",
            }),
        };
        const res = await getConfiguredNewCompetitionsInPrintCertificates(
          params
        );
        const data = Array.isArray(res) ? res : res?.data ?? [];
        const meta = res?.meta ?? {};
        setRawData(data);
        setFormattedData(formatData(data));
        setTotalItems(meta.total ?? data.length);
      } finally {
        setLoading(false);
      }
    },
    [searchText, filterCompetition, filterRoundType]
  );

  useEffect(() => {
    listData(currentPage, pageSize);
  }, [listData, currentPage, pageSize]);

  // Suppress ResizeObserver error - common issue with Ant Design tables
  useEffect(() => {
    const resizeObserverError = (e) => {
      if (
        e.message ===
          "ResizeObserver loop completed with undelivered notifications." ||
        e.message === "ResizeObserver loop limit exceeded"
      ) {
        e.stopImmediatePropagation();
      }
    };
    window.addEventListener("error", resizeObserverError);
    return () => {
      window.removeEventListener("error", resizeObserverError);
    };
  }, []);

  const approveNewCompetitionFunction = (formData) => {
    setLoading(true);
    addNewCompetitionInPrintCertificates(formData)
      .then((res) => {
        showNotification(
          "success",
          res?.message || "Configuration added successfully",
          enqueueSnackbar,
          closeSnackbar
        );
        listData(currentPage, pageSize);
      })
      .catch((err) => {
        showNotification(
          "error",
          err?.message || err?.errors || "Failed to add configuration",
          enqueueSnackbar,
          closeSnackbar
        );
      })
      .finally(() => {
        setLoading(false);
        setOnAddCompetition(false);
      });
  };

  const approveEditCompetitionFunction = (formData) => {
    if (!editingConfig?.id) return;
    setLoading(true);
    updateCertificateConfiguration(editingConfig.id, formData)
      .then((res) => {
        showNotification(
          "success",
          res?.message || "Configuration updated successfully",
          enqueueSnackbar,
          closeSnackbar
        );
        listData(currentPage, pageSize);
        cancelEditCompetition();
      })
      .catch((err) => {
        showNotification(
          "error",
          err?.message || err?.errors || "Failed to update configuration",
          enqueueSnackbar,
          closeSnackbar
        );
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleDeleteConfig = async (id) => {
    setLoading(true);
    deleteCertificateConfiguration(id)
      .then(() => {
        showNotification(
          "success",
          "Configuration deleted successfully",
          enqueueSnackbar,
          closeSnackbar
        );
        listData(currentPage, pageSize);
        setOnDelete(false);
        setSelectedConfigId(null);
      })
      .catch((err) => {
        showNotification(
          "error",
          err?.message || "Failed to delete configuration",
          enqueueSnackbar,
          closeSnackbar
        );
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    const loadCompetitionOptions = async () => {
      setLoadingFilters(true);
      try {
        const raw = await filterConfiguredCertificateTemplates(
          "competition",
          {}
        );
        const list = Array.isArray(raw)
          ? raw
          : raw?.data ?? raw?.results ?? raw?.items ?? [];
        setCompetitionFilterOptions(
          list.map((item) => ({
            label: item.filter_name ?? item.name,
            value: item.filter_id ?? item.id,
          }))
        );
      } finally {
        setLoadingFilters(false);
      }
    };
    loadCompetitionOptions();
  }, []);

  const parentColumns = [
    {
      title: "Configuration Name",
      dataIndex: "configuration_name",
      key: "configuration_name",
    },
    {
      title: "Competition Name",
      dataIndex: "competition_name",
      key: "competition_name",
    },
    { title: "Round Type", dataIndex: "round_type", key: "round_type" },
    {
      title: "Countries",
      dataIndex: "countries_display",
      key: "countries_display",
    },
    {
      title: "Organizations",
      dataIndex: "organizations_display",
      key: "organizations_display",
    },
    {
      title: "Roles",
      dataIndex: "roles_display",
      key: "roles_display",
    },
    ...(isAdmin()
      ? [
          {
            title: "Actions",
            key: "actions",
            width: 120,
            render: (_, record) => (
              <Box display="flex" gap={1} alignItems="center">
                <EditIcon
                  style={{ cursor: "pointer", color: "#144A94" }}
                  onClick={() => {
                    const raw =
                      rawData.find((r) => r.id === record.id) || record;
                    setEditingConfig(raw);
                    setOnEditCompetition(true);
                  }}
                />
                <DeleteIcon
                  style={{ cursor: "pointer", color: "#E83042" }}
                  onClick={() => {
                    setSelectedConfigId(record.id);
                    setOnDelete(true);
                  }}
                />
              </Box>
            ),
          },
        ]
      : []),
  ];

  const nestedColumns = [
    { title: "Award", dataIndex: "award", key: "award" },
    { title: "File Name", dataIndex: "file_name", key: "file_name" },
    // { title: "Roles", dataIndex: "roles", key: "roles" },
    {
      title: "Show Disclaimer",
      dataIndex: "show_disclaimer",
      key: "show_disclaimer",
    },
    {
      title: "Download",
      key: "download",
      width: 100,
      render: (_, row) =>
        row?.url ? (
          <IconButton
            size="small"
            onClick={() => window.open(row.url, "_blank")}
            sx={{ color: "#5E75C3" }}
            aria-label="Download template"
          >
            <DownloadIcon />
          </IconButton>
        ) : (
          "-"
        ),
    },
  ];

  const expandedRowRender = (record) => (
    <Table
      columns={nestedColumns}
      dataSource={record?.nestedData}
      pagination={false}
      rowKey={(row, index) => index}
    />
  );

  return (
    <Box className="wrapperBox">
      <LoadingBackdrop loading={loading} />
      <Collapse in={filtering}>
        <Grid
          container
          spacing={2}
          style={{ margin: "16px 2%", marginBottom: 0 }}
        >
          <Grid item>
            <ReusableSelect
              type="filter"
              label="Competition"
              width={220}
              marginRight={0}
              bgColor="#FFF"
              borderColor="#E0E0E0"
              state={filterCompetition?.value ?? ""}
              setState={(value) => {
                setFilterCompetition(
                  competitionFilterOptions.find(
                    (o) => o.value == value || String(o.value) === String(value)
                  ) ?? null
                );
                setCurrentPage(1);
              }}
              options={competitionFilterOptions.map((o) => ({
                value: o.value,
                option: o.label,
              }))}
              loading={loadingFilters}
            />
          </Grid>
          <Grid item>
            <ReusableSelect
              type="filter"
              label="Round Type"
              width={200}
              marginRight={0}
              bgColor="#FFF"
              borderColor="#E0E0E0"
              state={filterRoundType ?? ""}
              setState={(value) => {
                setFilterRoundType(value === "" ? null : Number(value));
                setCurrentPage(1);
              }}
              options={[
                { value: "", option: "All" },
                { value: 0, option: "Individual Round" },
                { value: 1, option: "Team Round" },
              ]}
            />
          </Grid>
          <Grid item alignItems="center" display="flex">
            <ReusableButton
              text="Reset filter"
              bgColor="#F16774"
              fontSize={14}
              height={42}
              br={6}
              onClick={() => {
                setFilterCompetition(null);
                setFilterRoundType(null);
                setCurrentPage(1);
              }}
              iconType="reset"
            />
          </Grid>
        </Grid>
      </Collapse>
      <Grid
        className="firstRowContainer"
        container
        justifyContent="space-between"
      >
        <div className="dashboardAndSelfBtnDiv">
          <ReusableButton
            text="Dashboard"
            fontSize={14}
            bgColor="#F16774"
            height={36}
            width={125}
            br={18}
            to="/dashboard"
            iconType="home"
          />
          <ChevronRightIcon />
          <ReusableButton
            text="Certificate Templates"
            fontSize={14}
            bgColor="#F16774"
            height={36}
            br={18}
            onClick={() => {}}
          />
        </div>
        <div className="viewAndFilterBtnDiv">
          <ViewAndFilterButton
            text="Filter"
            state={filtering}
            fontSize={14}
            height={42}
            onClick={() => setFiltering((prev) => !prev)}
          />
        </div>
      </Grid>
      <Grid className="tableContainer" container flexDirection={"column"}>
        <Grid
          display={"flex"}
          justifyContent={"space-between"}
          alignItems={"center"}
        >
          <NunitoText
            value="Configured Certificate Templates"
            fontSize={40}
            align={"left"}
            fontWeight={700}
            marginBottom={40}
            italic
            color="#144A94"
          />
          <div>
            {isAdmin() && (
              <ReusableButton
                marginBottom={40}
                text="Add New Competition"
                fontSize={15}
                bgColor="#5E75C3"
                height={46}
                marginRight={20}
                iconType="add"
                onClick={() => setOnAddCompetition(true)}
              />
            )}
          </div>
        </Grid>
        <div
          style={{
            width: "100%",
            marginBottom: "20px",
            position: "relative",
            overflow: "auto",
          }}
        >
          <Grid container spacing={2} style={{ marginBottom: 16 }}>
            <Grid item>
              <ReusableTextField
                type="search"
                width={320}
                height={54}
                bgColor="#F2F2F2"
                placeholder="Search by configuration or competition name"
                state={searchText}
                setState={(v) => {
                  setSearchText(v);
                  setCurrentPage(1);
                }}
                onBlur
              />
            </Grid>
          </Grid>
          <Table
            rowKey={(row, index) => row.id ?? index}
            columns={parentColumns}
            dataSource={formattedData}
            expandable={{ expandedRowRender, defaultExpandedRowKeys: ["0"] }}
            pagination={{
              current: currentPage,
              pageSize,
              total: totalItems,
              showSizeChanger: false,
              showTotal: (total) => `Total ${total} items`,
              onChange: (page, size) => {
                const newSize = size || 10;
                const pageSizeChanged = pageSizeRef.current !== newSize;
                if (pageSizeChanged) {
                  setCurrentPage(1);
                } else {
                  setCurrentPage(page);
                }
                setPageSize(newSize);
                pageSizeRef.current = newSize;
              },
            }}
            scroll={{ x: "auto" }}
            // style={{ width: "40%" }}
          />
        </div>
        {/* Add New Competition Modal */}
        {isAdmin() && (
          <Modal open={onAddCompetition} onClose={() => cancelAddCompetition()}>
            <Box component="div" sx={{ outline: "none" }} tabIndex={-1}>
              <AddNewCompetitionForCertificateModal
                approveFunction={approveNewCompetitionFunction}
                cancelApprove={cancelAddCompetition}
              />
            </Box>
          </Modal>
        )}
        {/* Edit Competition Configuration Modal */}
        {isAdmin() && (
          <Modal
            open={onEditCompetition}
            onClose={() => cancelEditCompetition()}
          >
            <Box component="div" sx={{ outline: "none" }} tabIndex={-1}>
              <AddNewCompetitionForCertificateModal
                editMode
                configId={editingConfig?.id}
                approveFunction={approveEditCompetitionFunction}
                cancelApprove={cancelEditCompetition}
              />
            </Box>
          </Modal>
        )}
      </Grid>
      <Modal open={onDelete} onClose={() => setOnDelete(false)}>
        <ConfirmDeleteModal
          cancelDelete={() => setOnDelete(false)}
          deleteFunction={handleDeleteConfig}
          id={selectedConfigId}
          objectName="this configuration"
        />
      </Modal>
    </Box>
  );
};

export default ConfigureCertificate;
